import React, { memo, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalHeader } from '@pwc/appkit-react/lib/Modal';
import { MdLink as LinkIcon } from 'react-icons/md';

import { SEARCH_TIMEOUT_DELAY } from '../../../helpMenu/helpMenu.constants';
import SearchDocs from '../../../helpMenu/subsections/searchDocs/searchDocs.component';

import {
  foundDocumentationSelector,
  isLoadingSelector,
} from '../../../helpMenu/subsections/searchDocs/store/selectors';
import {
  resetFoundDocumentation,
  searchForDocumentation,
} from '../../../helpMenu/subsections/searchDocs/store/actions';
import useDebounce from '../../hooks/useDebounce';
import { HelpContentPropTypes } from '../../../shared/proptypes';
import { editorPropTypes } from '../../propTypes';

const InsertInternalLinkButton = ({
  size,
  editor,
  foundDocumentation,
  isLoading,
  searchForDocumentation,
  resetFoundDocumentation,
}) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(useCallback(text => searchForDocumentation(`${text}`), [searchForDocumentation]), SEARCH_TIMEOUT_DELAY);
  const handleSearchChange = useCallback(event => {
    const text = event.target.value;
    setSearchText(text);
    if (text) {
      return debouncedSearch(text);
    }
    debouncedSearch.cancel();
    resetFoundDocumentation();
  }, [debouncedSearch, resetFoundDocumentation]);

  const [modalOpened, setModalOpened] = useState(false);
  const handleOpenClick = useCallback(() => setModalOpened(true), []);
  const handleClose = useCallback(() => {
    setModalOpened(false);
    setSearchText('');
    debouncedSearch.cancel();
    resetFoundDocumentation();
  }, [debouncedSearch, resetFoundDocumentation]);

  const handleAdd = useCallback(content => {
    setSearchText('');
    debouncedSearch.cancel();
    resetFoundDocumentation();

    setModalOpened(false);

    editor.insertInternalLink(content.contentId, content.title);
  }, [editor, debouncedSearch, resetFoundDocumentation]);

  return (
    <div>
      <LinkIcon onClick={handleOpenClick} size={size} />
      <Modal
        className="insertLink__fileUploadModal"
        visible={modalOpened}
        onCancel={handleClose}
        zIndex={100001}
      >
        <ModalHeader>
          <h5>Find help topic</h5>
        </ModalHeader>
        <ModalBody>
          <SearchDocs
            searchResults={foundDocumentation}
            onSearchChange={handleSearchChange}
            isLoading={isLoading}
            searchText={searchText}
            onClick={handleAdd}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

InsertInternalLinkButton.propTypes = {
  size: PropTypes.string,
  editor: editorPropTypes,
  resetFoundDocumentation: PropTypes.func.isRequired,
  searchForDocumentation: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  foundDocumentation: PropTypes.arrayOf(HelpContentPropTypes).isRequired,
};

export default memo(connect(
  state => ({
    isLoading: isLoadingSelector(state),
    foundDocumentation: foundDocumentationSelector(state),
  }),
  {
    searchForDocumentation,
    resetFoundDocumentation,
  }
)(InsertInternalLinkButton));

